import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Typography,
  withStyles,
  FormControl,
  InputLabel,
  FormHelperText,
  FormControlLabel,
  Checkbox,
  TextField
} from '@material-ui/core'
import React, { useMemo, useState } from 'react'
import { useMutation, useQuery } from 'react-apollo'
import { connect } from 'react-redux'
import { useForm, Controller } from 'react-hook-form'
import { yupResolver } from '@hookform/resolvers/yup'
import * as yup from 'yup'
import Select from 'react-select'

import { ErrorDialog } from 'src/components/reusable/ErrorDialog'
import Spinner from 'src/components/reusable/Spinner'
import { CREATE_SUPPORT_TASK } from 'src/gql/taskCommand'
import FormElementHelperTooltip from 'src/components/reusable/FormElementHelperTooltip'
import { ReduxState } from 'src/store/reducers'
import { getTranslation, Translate } from 'src/translation'
import { listAllOrgsGql } from 'src/gql/queries/listAllOrgs'
import { Channel, LIST_ORG_CHANNELS_QUERY } from 'src/gql/listOrgChannels'
import { isLPUser } from 'src/utils/common'
import { compose } from 'src/utils/common'

const createValidationSchema = () => {
  return yup.object({
    taskTitle: yup.string().required('Required'),
    taskMessage: yup.string().required('Required'),
    createSupportTaskOnBehalfOfOtherOrg: yup.boolean(),
    orgId: yup.string().test({
      name: 'conditional-required-org',
      message: 'Required',
      test: function (value, context) {
        const { createSupportTaskOnBehalfOfOtherOrg, channelId, email } = context.parent

        if (createSupportTaskOnBehalfOfOtherOrg && !value) {
          return false
        }

        if (channelId && !value) {
          return false
        }

        if (email && !value) {
          return false
        }

        return true
      }
    }),
    channelId: yup.string(),
    email: yup
      .string()
      .email('Invalid email')
      .test({
        name: 'conditional-required-email',
        message: 'Required',
        test: function (value, context) {
          const { createSupportTaskOnBehalfOfOtherOrg, orgId } = context.parent

          if (createSupportTaskOnBehalfOfOtherOrg && !value) {
            return false
          }

          if (orgId && !value) {
            return false
          }

          return true
        }
      })
  })
}

interface CreateSupportTaskProps {
  classes?: any
  onClose?: () => void
  callback: (chatId?: string) => void
  translate: Translate
  tubeColor: string
  userOrgId: string
  userEmail: string
}

const CreateSupportTaskDialog = (props: CreateSupportTaskProps) => {
  const { translate, callback, classes, onClose } = props
  const [isLoading, setIsLoading] = useState<boolean>(false)
  const [error, setError] = useState('')
  const [allOrgs, setAllOrgs] = useState([])
  const [allChannels, setAllChannels] = useState([])

  const { control, handleSubmit, formState, watch } = useForm({
    defaultValues: {
      taskTitle: '',
      taskMessage: '',
      createSupportTaskOnBehalfOfOtherOrg: false,
      orgId: '',
      channelId: '',
      email: ''
    },
    resolver: yupResolver(createValidationSchema()),
    mode: 'onChange'
  })

  const { errors } = formState

  const { loading: fetchingAllOrgsLoading } = useQuery(listAllOrgsGql, {
    onCompleted: (data) => {
      setAllOrgs(data?.listAllOrgs)
    }
  })

  const { loading: fetchingChannelsLoading } = useQuery<{ channels: Channel[] }>(LIST_ORG_CHANNELS_QUERY, {
    variables: {
      orgId: watch('orgId')
    },
    skip: !watch('orgId'),
    fetchPolicy: 'network-only',
    onCompleted: (data) => {
      if (data?.channels) {
        setAllChannels(data?.channels)
      }
    },
    onError: (error) => {
      setError(error.message)
    }
  })

  const [addSupportTicket] = useMutation(CREATE_SUPPORT_TASK, {
    onCompleted: (data: any) => {
      callback(data?.res?.taskId || data?.taskId)
      setIsLoading(false)
    }
  })

  const createSupportTaskOnBehalfOfOtherOrg = async (input) => {
    await addSupportTicket({
      variables: {
        input: {
          type: 'createSupportTaskForOtherOrg',
          payload: {
            taskTitle: input.taskTitle,
            comment: input.taskMessage,
            ticketReferenceUrl: window.location.href,
            orgId: input.orgId,
            channelId: input.channelId,
            assigneeEmail: input.email
          }
        }
      }
    })
    return
  }

  const createSupportTask = async (input) => {
    await addSupportTicket({
      variables: {
        input: {
          type: 'createSupportTask',
          payload: {
            taskTitle: input.taskTitle,
            comment: input.taskMessage,
            ticketReferenceUrl: window.location.href
          }
        }
      }
    })
  }

  const onHandleSubmit = async (values) => {
    setIsLoading(true)

    try {
      if (watch('createSupportTaskOnBehalfOfOtherOrg')) {
        await createSupportTaskOnBehalfOfOtherOrg(values)
      } else {
        await createSupportTask(values)
      }
    } catch (error) {
      setError(error?.networkError?.result?.message)
    } finally {
      setIsLoading(false)
    }
  }

  const channels = useMemo(() => {
    return (
      allChannels?.map((channel) => ({
        value: channel.id,
        label: channel.name
      })) || []
    )
  }, [allChannels])

  return (
    <>
      <Dialog disableBackdropClick={false} disableEscapeKeyDown={false} onClose={onClose} open={true}>
        <DialogTitle>{translate('request-assistant')}</DialogTitle>
        <Typography color="textSecondary" className="px-24">
          {translate('request-an-assistant-subtitle')}
        </Typography>
        <form onSubmit={handleSubmit(onHandleSubmit)}>
          <DialogContent>
            <div className={classes.dialog}>
              <Controller
                name="taskTitle"
                control={control}
                rules={{ required: 'Required' }}
                render={({ onChange, onBlur, value, name, ref }) => (
                  <TextField
                    onChange={onChange}
                    onBlur={onBlur}
                    value={value}
                    name={name}
                    fullWidth={true}
                    id="title"
                    className="mb-16"
                    label={
                      <div>
                        {translate('subject')} *
                        <FormElementHelperTooltip title="request-assitant-subject-instructions" />
                      </div>
                    }
                    error={!!errors.taskTitle}
                    helperText={errors.taskTitle?.message}
                    InputLabelProps={{
                      classes: {
                        root: classes.formLabel
                      }
                    }}
                  />
                )}
              />

              <Controller
                name="taskMessage"
                control={control}
                rules={{ required: 'Required' }}
                render={({ onChange, onBlur, value, name, ref }) => (
                  <TextField
                    onChange={onChange}
                    onBlur={onBlur}
                    value={value}
                    name={name}
                    fullWidth={true}
                    id="message"
                    className="mb-16"
                    multiline={true}
                    rows={3}
                    rowsMax={10}
                    label={
                      <div>
                        {translate('chat-message')} *
                        <FormElementHelperTooltip title="request-assitant-message-instructions" />
                      </div>
                    }
                    error={!!errors.taskMessage}
                    helperText={errors.taskMessage?.message}
                    InputLabelProps={{
                      classes: {
                        root: classes.formLabel
                      }
                    }}
                  />
                )}
              />

              {isLPUser(props.userEmail) && ['launchpeople', 'trainingtube'].includes(props.userOrgId) && (
                <>
                  <div className="mb-16 w-full">
                    <Controller
                      name="createSupportTaskOnBehalfOfOtherOrg"
                      control={control}
                      render={({ onChange, onBlur, value, name }) => (
                        <FormControlLabel
                          control={
                            <Checkbox
                              checked={!!value}
                              onChange={(e) => {
                                onChange(e.target.checked)
                              }}
                              name={name}
                              onBlur={onBlur}
                            />
                          }
                          label={translate('create-support-task-on-behalf-of-other-organization')}
                        />
                      )}
                    />
                  </div>

                  {watch('createSupportTaskOnBehalfOfOtherOrg') && (
                    <>
                      <div className="mb-16 w-full">
                        <FormControl error={!!errors.orgId} fullWidth={true}>
                          <InputLabel htmlFor="orgId" shrink={true} style={{ position: 'relative' }}>
                            {translate('organization')}
                          </InputLabel>
                          <Controller
                            name="orgId"
                            control={control}
                            render={({ onChange, onBlur, value }) => (
                              <Select
                                inputId="orgId"
                                name="orgId"
                                onBlur={onBlur}
                                options={
                                  allOrgs?.map((org) => ({
                                    value: org.orgId,
                                    label: org.name
                                  })) || []
                                }
                                isLoading={fetchingAllOrgsLoading}
                                isClearable={true}
                                className="react-select-container"
                                classNamePrefix="react-select"
                                value={
                                  allOrgs
                                    ?.map((org) => ({
                                      value: org.orgId,
                                      label: org.name
                                    }))
                                    ?.find((option) => option.value === value) || null
                                }
                                onChange={(option) => {
                                  onChange(option?.value || '')
                                }}
                                styles={{
                                  control: (base) => ({
                                    ...base,
                                    minHeight: '40px',
                                    marginTop: '8px',
                                    backgroundColor: 'transparent',
                                    borderColor: errors.orgId ? '#f44336' : 'rgba(0, 0, 0, 0.23)',
                                    '&:hover': {
                                      borderColor: errors.orgId ? '#f44336' : 'rgba(0, 0, 0, 0.87)'
                                    }
                                  }),
                                  menu: (base) => ({
                                    ...base,
                                    zIndex: 1500
                                  }),
                                  menuPortal: (base) => ({
                                    ...base,
                                    zIndex: 1500
                                  })
                                }}
                                menuPortalTarget={document.body}
                                menuPosition={'fixed'}
                                menuPlacement="auto"
                              />
                            )}
                          />
                          {errors.orgId && <FormHelperText error={true}>{errors.orgId.message}</FormHelperText>}
                        </FormControl>
                      </div>

                      <div className="mb-16 w-full">
                        <FormControl error={!!errors.channelId} fullWidth={true}>
                          <InputLabel htmlFor="channelId" shrink={true} style={{ position: 'relative' }}>
                            {translate('channel')}
                          </InputLabel>
                          <Controller
                            name="channelId"
                            control={control}
                            render={({ onChange, onBlur, value }) => (
                              <Select
                                inputId="channelId"
                                name="channelId"
                                onBlur={onBlur}
                                options={channels}
                                isLoading={fetchingChannelsLoading}
                                isClearable={true}
                                className="react-select-container"
                                classNamePrefix="react-select"
                                value={channels?.find((option) => option.value === value) || null}
                                onChange={(option) => {
                                  onChange(option?.value || '')
                                }}
                                styles={{
                                  control: (base) => ({
                                    ...base,
                                    minHeight: '40px',
                                    marginTop: '8px',
                                    backgroundColor: 'transparent',
                                    borderColor: errors.orgId ? '#f44336' : 'rgba(0, 0, 0, 0.23)',
                                    '&:hover': {
                                      borderColor: errors.orgId ? '#f44336' : 'rgba(0, 0, 0, 0.87)'
                                    }
                                  }),
                                  menu: (base) => ({
                                    ...base,
                                    zIndex: 1500
                                  }),
                                  menuPortal: (base) => ({
                                    ...base,
                                    zIndex: 1500
                                  })
                                }}
                                menuPortalTarget={document.body}
                                menuPosition={'fixed'}
                                menuPlacement="auto"
                              />
                            )}
                          />
                          {errors.channelId && <FormHelperText error={true}>{errors.channelId.message}</FormHelperText>}
                        </FormControl>
                      </div>

                      <Controller
                        name="email"
                        control={control}
                        render={({ onChange, onBlur, value, name, ref }) => (
                          <TextField
                            onChange={onChange}
                            onBlur={onBlur}
                            value={value}
                            name={name}
                            fullWidth={true}
                            id="email"
                            className="mb-16"
                            label={translate('email')}
                            error={!!errors.email}
                            helperText={errors.email?.message}
                          />
                        )}
                      />
                    </>
                  )}
                </>
              )}
            </div>
          </DialogContent>
          <DialogActions>
            <Button name="Cancel" id="cancel-btn" onClick={onClose}>
              {translate('cancel')}
            </Button>
            <Button variant="contained" name="Request" id="request-form-btn" type="submit" color="primary">
              {isLoading ? (
                <Spinner />
              ) : (
                <span
                  style={{
                    color: 'white'
                  }}
                >
                  {translate('request')}
                </span>
              )}
            </Button>
          </DialogActions>
        </form>
      </Dialog>

      <ErrorDialog open={Boolean(error)} message={error} onClose={() => setError('')} />
    </>
  )
}

const mapStateToProps = (state: ReduxState) => ({
  tubeColor: state.auth.tube.color,
  user: state.auth.user?.profile,
  userOrgId: state.auth.user?.orgId,
  translate: getTranslation(state.translation.lang),
  userEmail: state.auth.user?.email
})

const styles = () => ({
  dialog: {
    flexDirection: 'column' as 'column',
    height: '100%',
    display: 'flex',
    marginTop: 10
  },
  tooltip: {
    backgroundColor: '#e5e5e5',
    color: '#000',
    fontSize: '12px'
  },
  formLabel: {
    zIndex: 1,
    position: 'relative' as 'relative'
  },
  infoIcon: {
    backgroundColor: '#e5e5e5',
    padding: '1px 6px',
    marginLeft: '5px',
    borderRadius: '100%',
    fontSize: '1.3rem',
    color: '#000',
    border: '1px solid #eeee'
  }
})

export default compose(connect(mapStateToProps), withStyles(styles))(CreateSupportTaskDialog)
